import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, X, Building, ExternalLink, ArrowUpDown, HelpCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { 
  getLocationCompSets, 
  createLocationCompSet, 
  updateLocationCompSet, 
  deleteLocationCompSet
} from '../../utils/api';
import { getLocationsByOrgId } from '../../utils/locationApi';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import { useOptimisticNotifications } from '../../hooks/useOptimisticNotifications';
import AdminTabs from './AdminTabs';

const LocationCompSets = () => {
  const { 
    currentOrganization, // The currently selected organization object { id, code, name, workspaceId, ... }
    isLoading: isLoadingUserSettings // Loading state from the context hook
  } = useUserSettingsContext();
  
  const [compSets, setCompSets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCompSet, setEditingCompSet] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'compSetName', direction: 'asc' });
  const [urlError, setUrlError] = useState('');
  const notify = useOptimisticNotifications();

  // Form state
  const [formData, setFormData] = useState({
    orgId: '',
    locationId: '',
    compSetName: '',
    googleTravelUrl: ''
  });

  // Load locations when organization is available
  useEffect(() => {
    const fetchLocations = async () => {
      if (currentOrganization && !isLoadingUserSettings) {
        try {
          const locationsData = await getLocationsByOrgId(currentOrganization.id);
          setLocations(Array.isArray(locationsData) ? locationsData : []);
        } catch (error) {
          console.error('Error fetching locations:', error);
          notify.error('Failed to load locations');
          setLocations([]);
        }
      } else {
        setLocations([]);
      }
    };

    fetchLocations();
  }, [currentOrganization, isLoadingUserSettings]);

  // Load comp sets when organization or location changes
  useEffect(() => {
    const fetchCompSets = async () => {
      if (currentOrganization && !isLoadingUserSettings) {
        setIsLoading(true);
        try {
          const compSetsData = await getLocationCompSets(currentOrganization.id, selectedLocationId || null);
          setCompSets(Array.isArray(compSetsData) ? compSetsData : []);
        } catch (error) {
          console.error('Error fetching comp sets:', error);
          notify.error('Failed to load comp sets');
          setCompSets([]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCompSets();
  }, [currentOrganization, selectedLocationId, isLoadingUserSettings]);

  // Update form data when organization changes
  useEffect(() => {
    if (currentOrganization) {
      setFormData(prev => ({
        ...prev,
        orgId: currentOrganization.id
      }));
    }
  }, [currentOrganization]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedCompSets = [...compSets].sort((a, b) => {
    if (sortConfig.key) {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';
      const comparison = aVal.toString().localeCompare(bVal.toString());
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    }
    return 0;
  });

  const filteredCompSets = sortedCompSets.filter(compSet => 
    compSet.compSetName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    compSet.googleTravelUrl?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddCompSet = () => {
    setEditingCompSet(null);
    setFormData({
      orgId: currentOrganization?.id || '',
      locationId: selectedLocationId || (locations.length > 0 ? locations[0].id : ''),
      compSetName: '',
      googleTravelUrl: ''
    });
    setShowAddForm(true);
  };

  const handleEditCompSet = (compSet) => {
    setEditingCompSet(compSet);
    setFormData({
      id: compSet.id,
      orgId: compSet.orgId,
      locationId: compSet.locationId,
      compSetName: compSet.compSetName || '',
      googleTravelUrl: compSet.googleTravelUrl || ''
    });
    setShowAddForm(true);
  };

  const handleDeleteCompSet = async (id) => {
    if (window.confirm('Are you sure you want to delete this competitor set? This action cannot be undone.')) {
      // Store original state for potential revert
      const originalCompSets = [...compSets];
      
      // Optimistically remove from UI
      setCompSets(compSets.filter(compSet => compSet.id !== id));
      
      try {
        await notify.delete(
          'Competitor set',
          deleteLocationCompSet(id),
          {
            revertAction: () => {
              setCompSets(originalCompSets);
            }
          }
        );
      } catch (error) {
        console.error('Error deleting comp set:', error);
        // Error handling already done by notify.delete
      }
    }
  };

  const handleSaveCompSet = async (e) => {
    e.preventDefault();
    
    if (!formData.orgId || !formData.locationId) {
      toast.error('Organization and Location are required');
      return;
    }

    // Validate Google Travel URL before saving
    if (formData.googleTravelUrl) {
      const validation = validateGoogleTravelUrl(formData.googleTravelUrl);
      if (!validation.isValid) {
        setUrlError(validation.message);
        toast.error('Please fix the URL format before saving');
        return;
      }
    }

    // Store original state for potential revert
    const originalCompSets = [...compSets];
    const isEditing = Boolean(editingCompSet);
    
    try {
      if (isEditing) {
        // Optimistically update in UI
        setCompSets(compSets.map(compSet => compSet.id === formData.id ? { ...compSet, ...formData } : compSet));
        
        await notify.update(
          'Competitor set',
          updateLocationCompSet(formData),
          {
            revertAction: () => {
              setCompSets(originalCompSets);
            }
          }
        );
      } else {
        // For create operations, we need to wait for the server response to get the ID
        await notify.create(
          'Competitor set',
          createLocationCompSet(formData),
          {
            onSuccess: (savedCompSet) => {
              setCompSets([...compSets, savedCompSet]);
            },
            revertAction: () => {
              setCompSets(originalCompSets);
            }
          }
        );
      }
      
      setShowAddForm(false);
      setEditingCompSet(null);
      setFormData({
        orgId: currentOrganization?.id || '',
        locationId: selectedLocationId || '',
        compSetName: '',
        googleTravelUrl: ''
      });
      setUrlError('');
    } catch (error) {
      console.error('Error saving comp set:', error);
      // Error handling already done by notify.create/update
    }
  };

  const getLocationName = (locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    return location?.name || `Location ${locationId}`;
  };

  // Function to clean URL by removing query parameters (everything after ?)
  const cleanUrl = (url) => {
    if (!url) return '';
    const questionMarkIndex = url.indexOf('?');
    return questionMarkIndex !== -1 ? url.substring(0, questionMarkIndex) : url;
  };

  // Function to validate Google Travel URL format
  const validateGoogleTravelUrl = (url) => {
    if (!url) return { isValid: true, message: '' }; // Empty URL is allowed
    
    const cleanedUrl = cleanUrl(url);
    const googleTravelPattern = /^https:\/\/www\.google\.com\/travel\/hotels\/entity\/[A-Za-z0-9_-]+$/;
    
    if (!googleTravelPattern.test(cleanedUrl)) {
      return {
        isValid: false,
        message: 'Please enter a valid Google Travel hotel URL. Format should be: https://www.google.com/travel/hotels/entity/[hotel-id]'
      };
    }
    
    return { isValid: true, message: '' };
  };

  // Handle URL input change with cleaning and validation
  const handleUrlChange = (e) => {
    const rawUrl = e.target.value;
    const cleanedUrl = cleanUrl(rawUrl);
    
    // Validate the URL format
    const validation = validateGoogleTravelUrl(cleanedUrl);
    setUrlError(validation.message);
    
    setFormData({ ...formData, googleTravelUrl: cleanedUrl });
  };

  // Show loading if user settings are still loading
  if (isLoadingUserSettings) {
    return (
      <div className="text-center py-12">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" style={{animationDuration: '0.4s'}}></div>
        <p className="mt-4 text-gray-600 dark:text-gray-400">Loading user settings...</p>
      </div>
    );
  }

  // Show message if no organization is selected
  if (!currentOrganization) {
    return (
      <div className="text-center py-8">
        <Building className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">No Organization Selected</h3>
        <p className="text-gray-600 dark:text-gray-400">Please select an organization to manage competitor sets</p>
      </div>
    );
  }

  return (
    <div>
      <AdminTabs />
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
          Location Competitor Sets - {currentOrganization.name}
        </h2>
        <button
          onClick={handleAddCompSet}
          disabled={!currentOrganization}
          className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center"
        >
          <Plus size={18} className="mr-2" />
          Add Competitor Set
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Location (Optional)
          </label>
          <select
            value={selectedLocationId}
            onChange={(e) => setSelectedLocationId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">All Locations</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Search
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search competitor sets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Competitor Sets Table */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" style={{animationDuration: '0.4s'}}></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading competitor sets...</p>
        </div>
      ) : (
        <div className="overflow-auto max-h-[70vh]">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => handleSort('compSetName')}
                >
                  <div className="flex items-center">
                    Competitor Name
                    <ArrowUpDown size={14} className="ml-1" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Google Travel URL
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center">
                    Created
                    <ArrowUpDown size={14} className="ml-1" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredCompSets.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    No competitor sets found
                  </td>
                </tr>
              ) : (
                filteredCompSets.map((compSet) => (
                  <tr key={compSet.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {compSet.compSetName || 'Unnamed Competitor'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {getLocationName(compSet.locationId)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {compSet.googleTravelUrl ? (
                        <div className="flex items-center">
                          <a
                            href={compSet.googleTravelUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 truncate max-w-xs"
                            title={compSet.googleTravelUrl}
                          >
                            {compSet.googleTravelUrl.length > 40 
                              ? `${compSet.googleTravelUrl.substring(0, 40)}...` 
                              : compSet.googleTravelUrl
                            }
                          </a>
                          <ExternalLink size={14} className="ml-1 text-gray-400" />
                        </div>
                      ) : (
                        <span className="text-gray-400">No URL</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {compSet.createdAt ? new Date(compSet.createdAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <button
                        onClick={() => handleEditCompSet(compSet)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                        title="Edit Competitor Set"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteCompSet(compSet.id)}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete Competitor Set"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 px-6 py-4">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingCompSet ? 'Edit Competitor Set' : 'Add New Competitor Set'}
              </h3>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setEditingCompSet(null);
                  setUrlError('');
                }}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSaveCompSet} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Organization
                  </label>
                  <input
                    type="text"
                    value={currentOrganization?.name || ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Location *
                  </label>
                  <select
                    value={formData.locationId}
                    onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Location</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Competitor Name
                  </label>
                  <input
                    type="text"
                    value={formData.compSetName}
                    onChange={(e) => setFormData({ ...formData, compSetName: e.target.value })}
                    placeholder="e.g. Hampton Inn & Suites"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <div className="flex items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Google Travel URL
                    </label>
                    <div className="group relative ml-2">
                      <HelpCircle size={16} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-96 px-4 py-3 text-sm text-white bg-gray-900 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50">
                        <div className="text-left">
                          <div className="flex items-center mb-2">
                            <span className="mr-2">🧭</span>
                            <strong>How to get the hotel link from Google Travel:</strong>
                          </div>
                          <div className="space-y-1 text-xs">
                            <div>1. Go to Google Travel Hotels</div>
                            <div>2. Search for and click on the hotel you're interested in</div>
                            <div>3. On the hotel card, click the open icon (usually in the top-right) to open it in a new tab</div>
                            <div>4. In the new tab, copy the full URL from your browser's address bar</div>
                            <div className="ml-4 text-gray-300">• It should look like: https://www.google.com/travel/hotels/entity/...</div>
                            <div>5. Paste that link here</div>
                            <div className="mt-2 pt-2 border-t border-gray-600 text-gray-300">
                              <em>Note: Query parameters (text after ?) will be automatically removed</em>
                            </div>
                          </div>
                        </div>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                  <input
                    type="url"
                    value={formData.googleTravelUrl}
                    onChange={handleUrlChange}
                    placeholder="https://www.google.com/travel/hotels/entity/ChgI9eP9p47FnqzHARoLL2cvMXdmMzkxbGgQAQ"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 dark:bg-gray-700 dark:text-white ${
                      urlError 
                        ? 'border-red-300 dark:border-red-600 focus:ring-red-500' 
                        : 'border-gray-300 dark:border-gray-600 focus:ring-orange-500'
                    }`}
                  />
                  {urlError && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {urlError}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingCompSet(null);
                    setUrlError('');
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
                >
                  {editingCompSet ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationCompSets; 